import React, {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { useSelector } from "react-redux";
import { State } from "store";
import { verifyAndFormatAggregates } from "utilities/filters/deviceFilters";
import { useTranslation } from "utilities/i18n";
import { OperationContext } from "@urql/core";
import {
  DevicesLocationQuery,
  useProvisioningAccountsQuery
} from "generated/graphql";
import { noOpNotifyContext } from "polygraphql";
import {
  areFiltersSelected,
  DeviceFilterState,
  LoadingIndicator,
  NoResultsIndicator,
  NoResultsIndicatorAnimation,
  useDeviceFilterValue
} from "elements";
import { DeviceCardProps } from "routes/Manage/Inventory/List/DeviceCard";
import {
  DeviceRow,
  generateTableHeaders,
  getDeviceColumnTranslations
} from "routes/Manage/Inventory/List/TableView";
import {
  CheckTableHeader,
  ClearFiltersHeader,
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableHeader,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export interface TableViewSelectedDevice {
  id: string;
  serial: string | null;
  name: string | null;
  hardwareModel: string | null;
  lastConfigRequestDate: string;
}

export interface TableViewHeadingProps {
  selectedDeviceIDs?: Array<string>;
  count?: number;
  totalCount?: number;
  fetching?: boolean;
}

interface AssignmentTableView {
  headingChildren?: ReactElement<TableViewHeadingProps>;
}

export const AssignmentDeviceTableView: React.FC<{
  setCursor: Dispatch<SetStateAction<number>>;
  refreshDevices: (opts?: Partial<OperationContext> | undefined) => void;
  devices: Array<DeviceCardProps>;
  aggregates: DevicesLocationQuery["deviceAggregations"];
  loading: boolean;
  stale: boolean;
  count: number;
  totalCount: number;
  tableKey: keyof typeof TABLES;
  headingChildren?: AssignmentTableView["headingChildren"];
  setSelected?: (args: Array<TableViewSelectedDevice>) => void;
}> = ({
  setCursor,
  devices,
  aggregates,
  loading,
  stale,
  count,
  totalCount,
  tableKey,
  headingChildren,
  setSelected
}) => {
  const { t } = useTranslation();
  const verifyAndFormat = verifyAndFormatAggregates(aggregates);
  const tenantId = useTenantId();
  const [selectedDeviceIDs, setSelectedDeviceIDs] = useState<Array<string>>([]);
  const [filterState, setFilter, resetFilters] = useDeviceFilterValue();
  const [pageLoading, setPageLoading] = useState(false);
  const { data: provData } = useProvisioningAccountsQuery({
    variables: { tenantUuid: tenantId },
    context: noOpNotifyContext
  });
  const getValue = (bucket: { value: string; count: number }) => bucket.value;
  const { NAME: TABLE_NAME, COLUMNS, HIDDEN } = TABLES[
    tableKey
  ] as typeof TABLES.DEVICES;

  const ALL_COLUMNS = { ...COLUMNS, ...HIDDEN };
  const filterOptions = {
    [ALL_COLUMNS.MODEL]: verifyAndFormat("hardwareModel").map(getValue),
    [ALL_COLUMNS.SITE]: verifyAndFormat("site").map(getValue),
    [ALL_COLUMNS.VERSION]: verifyAndFormat("softwareVersion").map(getValue)
  };

  // reset selected device IDs if tenant ID changes.
  // Unlikely to happen in assignment modal, but seems prudent to leave in place
  useEffect(() => {
    setSelectedDeviceIDs([]);
  }, [tenantId]);

  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const fetching = loading || stale;
  useEffect(() => {
    if (!fetching) {
      setPageLoading(false);
    }
  }, [fetching]);

  useEffect(() => {
    if (setSelected) {
      const selectedDevices = devices
        .filter(device => selectedDeviceIDs.includes(device.id))
        .map(device => ({
          id: device.id,
          serial: device.serialNumber,
          name: device.name,
          hardwareModel: device.hardwareModel,
          lastConfigRequestDate: device.lastConfigRequestDate
        }));

      setSelected(selectedDevices);
    }
    // omit devices from deps, causes useEffect to call infinitely with no benefit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceIDs, setSelected]);

  // required to help determine "primary device" status
  const account = provData?.getProvisioningAccounts?.[0];
  const provisioningDisabled = !account || !!account.isDisabled;

  // Loads all translations for all columns.
  const columnTranslations = getDeviceColumnTranslations();

  const updateCursor: Dispatch<SetStateAction<number>> = cursor => {
    setPageLoading(true);
    setCursor(cursor);
  };

  const updateFilter = (newFilterState: DeviceFilterState) => {
    setCursor(0);
    setFilter(newFilterState);
  };

  const shownCols = getColumns(shownColumns, columnTranslations);
  const hiddenCols = getColumns(hiddenColumns, columnTranslations);

  // checkbox selection
  const toggleItem = toggleItemWithSet(
    devices.length,
    selectedDeviceIDs,
    setSelectedDeviceIDs
  );
  const itemChecked = itemCheckedWithSet(selectedDeviceIDs);

  if (!fetching && !totalCount) {
    return <NoResultsIndicatorAnimation title={t("NO_DEVICES")} />;
  }

  const clonedHeadingChildren = React.isValidElement(headingChildren)
    ? React.cloneElement<TableViewHeadingProps>(headingChildren, {
        selectedDeviceIDs: selectedDeviceIDs.filter(id => id !== HEADER_ID),
        count,
        totalCount,
        fetching
      })
    : null;

  return (
    <>
      <div className="table-heading">{clonedHeadingChildren}</div>
      <div className="table-actions mb-15">
        <TableSearchEdit
          search={{
            onSearch: term => {
              if (term !== undefined) {
                setCursor(0);
              }
            }
          }}
          edit={{
            tableName: TABLE_NAME,
            shownColumns: shownCols,
            hiddenColumns: hiddenCols
          }}
        />
      </div>
      <div className="table-container">
        <table className="table custom-table table-filterable">
          <TableHeader>
            {shownColumns.length && (
              <CheckTableHeader
                checked={itemChecked(HEADER_ID)}
                onCheck={() =>
                  toggleAll(
                    devices.map(device => device.id),
                    selectedDeviceIDs,
                    setSelectedDeviceIDs
                  )
                }
              />
            )}
            {generateTableHeaders(
              shownCols,
              filterState,
              updateFilter,
              filterOptions
            )}
            <ClearFiltersHeader
              showClearFilters={areFiltersSelected(filterState)}
              onClearFilters={resetFilters}
            />
          </TableHeader>
          <TableBody
            loading={fetching && !pageLoading}
            cols={shownCols.length}
            accommodateScrollbar
          >
            {devices.map((device, index) => (
              <DeviceRow
                {...device}
                key={device.id}
                accountProvisioningDisabled={provisioningDisabled}
                checked={itemChecked(device.id)}
                connectionStatus={device?.connected}
                count={count}
                index={index}
                listLength={devices.length}
                onCheck={() => toggleItem(device.id)}
                setCursor={updateCursor}
                showChecks={!!shownColumns.length}
                shownColumns={shownCols.map(column => column.key)}
              />
            ))}
          </TableBody>
        </table>
        {!fetching && !devices.length ? (
          <>
            <NoResultsIndicator className="inline" title={t("NO_DEVICES")} />
          </>
        ) : null}
        {fetching && pageLoading ? (
          <LoadingIndicator className="inline" key="loading" />
        ) : null}
      </div>
    </>
  );
};
